import { Fragment, useEffect, useState } from 'react'
import { Outlet, Navigate, NavLink, matchPath, useLocation, useSearchParams } from 'react-router'
import {
  Alert,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import PeopleIcon from '@mui/icons-material/People'
import BusinessIcon from '@mui/icons-material/Business'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { isOrgAdminRole } from 'chat-shared'
import type { OrgFeature, OrgRole } from 'chat-shared'
import { api } from '../../api/client.js'
import { useAuth } from '../../auth/AuthContext.js'
import {
  ORGANIZATION_SECTIONS,
  OrganizationSection,
} from '../../components/OrganizationManager.js'

const systemSections = [
  { path: 'users', label: 'Nutzer', icon: <PeopleIcon fontSize="small" /> },
  { path: 'organizations', label: 'Organisationen', icon: <BusinessIcon fontSize="small" /> },
  { path: 'assignments', label: 'Zuweisungen', icon: <AssignmentIcon fontSize="small" /> },
  { path: 'models', label: 'Modelle', icon: <SmartToyIcon fontSize="small" /> },
]

// Org-Admins sehen nur die Organisationsverwaltung — die System-Sections
// (Nutzer, Zuweisungen, Modelle) bleiben Systemadmins vorbehalten.
const orgAdminSections = systemSections.filter((section) => section.path === 'organizations')

// Einheitlicher Look aller Menüeinträge: abgerundet, aktiver Eintrag farblich
// hervorgehoben (NavLink setzt die Klasse „active“).
const navButtonSx = {
  borderRadius: 1.5,
  mx: 1,
  '&.active': {
    bgcolor: 'action.selected',
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: 'primary.main',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  },
}

/**
 * Einheitliche Sicht auf eine Organisation im Einstellungsbereich —
 * Systemadmins bekommen sie aus /admin/organizations (mit Zählern/Owner),
 * Org-Admins aus /organizations (eigene Orgs mit Rolle).
 */
export type AdminOrgEntry = {
  id: string
  name: string
  createdAt: string
  enabledFeatures: Array<OrgFeature>
  /** Eigene Mitgliedsrolle — undefined beim Zugriff als Systemadmin. */
  role?: OrgRole
  memberCount?: number
  groupCount?: number
  ownerEmail?: string
  ownerDisplayName?: string
}

/** Zuordnung Feature-Freischaltung → Org-Unterbereich in der Navigation. */
const SECTION_FEATURE: Partial<Record<OrganizationSection, OrgFeature>> = {
  'mcp-servers': 'mcp-management',
  'api-keys': 'api-keys',
  teams: 'teams',
  slack: 'slack',
}

/** Welche Org-Unterbereiche für diese Org sichtbar sind (Rolle + Features). */
export const visibleOrgSections = (
  org: AdminOrgEntry,
  isSystemAdmin: boolean,
): Array<(typeof ORGANIZATION_SECTIONS)[number]> =>
  ORGANIZATION_SECTIONS.filter((section) => {
    const feature = SECTION_FEATURE[section.path]
    return feature === undefined || isSystemAdmin || org.enabledFeatures.includes(feature)
  })

/**
 * Von AdminLayout über das Outlet bereitgestellt: die Organisationsliste wird
 * zentral geladen (sie speist auch die Menü-Ordner) und von den Sections nach
 * Anlegen/Umbenennen/Löschen/Feature-Änderung per reloadOrganizations
 * aktualisiert.
 */
export type AdminOutletContext = {
  organizations: Array<AdminOrgEntry>
  organizationsLoaded: boolean
  reloadOrganizations: () => void
}

/**
 * Rahmen des Einstellungsbereichs (/settings): linke Navigationsleiste, der
 * Inhalt der aktiven Section rendert ins Outlet (Routen siehe App.tsx).
 * Systemadmins sehen alle Sections und alle Organisationen; Org-Owner/-Admins
 * nur ihre eigenen Organisationen und dort nur die per Feature-Freischaltung
 * aktivierten Unterbereiche. Unter „Organisationen“ erscheint jede
 * Organisation wie ein Ordner: aufklappbar mit ihren Unterbereichen.
 */
export const AdminLayout = () => {
  const location = useLocation()
  const { user, loading } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [notice, setNotice] = useState<
    { severity: 'success' | 'error'; message: string } | undefined
  >(undefined)

  const isSystemAdmin = user?.role === 'system-admin'

  const [organizations, setOrganizations] = useState<Array<AdminOrgEntry>>([])
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false)
  const [expandedOrgIds, setExpandedOrgIds] = useState<ReadonlySet<string>>(new Set())

  const reloadOrganizations = () => {
    const load: Promise<Array<AdminOrgEntry>> = isSystemAdmin
      ? api.listAdminOrganizations()
      : api
          .listOrganizations()
          .then((orgs) =>
            orgs
              .filter((org) => isOrgAdminRole(org.role))
              .map((org) => ({
                id: org.id,
                name: org.name,
                createdAt: org.createdAt,
                enabledFeatures: org.enabledFeatures,
                role: org.role,
              })),
          )
    load
      .then(setOrganizations)
      .catch(() => setOrganizations([]))
      .finally(() => setOrganizationsLoaded(true))
  }

  useEffect(() => {
    if (!loading && user) reloadOrganizations()
  }, [loading, user?.id, isSystemAdmin])

  // Die Organisation der aktuellen Route im Menü aufklappen — greift auch bei
  // Direktaufruf der URL oder Rückkehr vom MCP-OAuth-Consent.
  const activeOrgId = matchPath('/settings/organizations/:orgId/*', location.pathname)?.params
    .orgId
  useEffect(() => {
    if (!activeOrgId) return
    setExpandedOrgIds((prev) => (prev.has(activeOrgId) ? prev : new Set([...prev, activeOrgId])))
  }, [activeOrgId])

  const toggleOrg = (orgId: string) => {
    setExpandedOrgIds((prev) => {
      const next = new Set(prev)
      if (!next.delete(orgId)) next.add(orgId)
      return next
    })
  }

  // Rückkehr vom MCP-Server-Consent-Screen: ?oauth=success|error (siehe
  // apps/chat-api/src/http/mcpOAuthCallback.ts — leitet auf die Detailansicht
  // der betroffenen Organisation zurück).
  useEffect(() => {
    const oauthResult = searchParams.get('oauth')
    if (!oauthResult) return
    setNotice(
      oauthResult === 'success'
        ? { severity: 'success', message: 'MCP-Server erfolgreich autorisiert.' }
        : { severity: 'error', message: 'Autorisierung fehlgeschlagen oder abgebrochen.' },
    )
    setSearchParams((prev) => {
      prev.delete('oauth')
      return prev
    })
  }, [])

  if (loading) return null
  // Zugriffs-Guard: weder Systemadmin noch Org-Owner/-Admin in irgendeiner
  // Organisation ⇒ zurück zur Startseite. Erst nach dem Laden der Orgs
  // entscheiden, sonst würden Org-Admins beim ersten Render rausfliegen.
  if (!isSystemAdmin) {
    if (!organizationsLoaded) return null
    if (!organizations.some((org) => org.role !== undefined && isOrgAdminRole(org.role))) {
      return <Navigate to="/" replace />
    }
  }

  const sections = isSystemAdmin ? systemSections : orgAdminSections

  return (
    <Box display="flex" height="100%">
      <Box
        width={250}
        flexShrink={0}
        borderRight={1}
        borderColor="divider"
        display="flex"
        flexDirection="column"
        overflow="auto"
        bgcolor="background.paper"
      >
        <Typography
          variant="overline"
          color="text.secondary"
          px={2.5}
          pt={2}
          pb={0.5}
          letterSpacing="0.08em"
        >
          Einstellungen
        </Typography>
        <List dense sx={{ pt: 0 }}>
          {sections.map((section) => (
            <Fragment key={section.path}>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={section.path}
                  end={section.path === 'organizations'}
                  sx={navButtonSx}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.label} />
                </ListItemButton>
              </ListItem>
              {section.path === 'organizations' &&
                organizations.map((org) => {
                  const expanded = expandedOrgIds.has(org.id)
                  return (
                    <Fragment key={org.id}>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ ...navButtonSx, pl: 3 }} onClick={() => toggleOrg(org.id)}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {expanded ? (
                              <FolderOpenIcon fontSize="small" />
                            ) : (
                              <FolderIcon fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={org.name}
                            primaryTypographyProps={{ noWrap: true }}
                          />
                          {expanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List dense disablePadding>
                          {visibleOrgSections(org, isSystemAdmin).map((orgSection) => (
                            <ListItem key={orgSection.path} disablePadding>
                              <ListItemButton
                                component={NavLink}
                                to={`organizations/${org.id}/${orgSection.path}`}
                                sx={{ ...navButtonSx, pl: 7 }}
                              >
                                <ListItemText
                                  primary={orgSection.label}
                                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Fragment>
                  )
                })}
            </Fragment>
          ))}
        </List>
      </Box>
      <Box flex={1} minWidth={0} overflow="auto" bgcolor="action.hover">
        <Box maxWidth={1000} mx="auto" px={{ xs: 2, md: 4 }} py={{ xs: 2, md: 3.5 }}>
          {notice && (
            <Alert severity={notice.severity} onClose={() => setNotice(undefined)} sx={{ mb: 3 }}>
              {notice.message}
            </Alert>
          )}
          <Outlet context={{ organizations, organizationsLoaded, reloadOrganizations }} />
        </Box>
      </Box>
    </Box>
  )
}
